import React, { useState, useEffect } from 'react';
import { Container, Typography, FormControl, Select, MenuItem, Box, Button } from '@mui/material';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto'; // Automatically imports the necessary Chart.js components
import { apiService } from "../components/ReasuableFuncations/api";
import pptxgen from "pptxgenjs";

const RiskLineChart = () => {
  // State to hold risk data, selected risk type, loading state, and error state
  const [riskData, setRiskData] = useState([]);
  const [selectedRiskType, setSelectedRiskType] = useState('All');
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state

  const riskTypes = ['All', 'Financial', 'Operational', 'Strategic'];

  const fetchRisks = async () => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (!userData) {
      setError('User data not found');
      setLoading(false);
      return;
    }

    const { regionalNetworkOption, countryOption } = userData;
    try {
      // Using the correct endpoint from your server routes
      const response = await apiService.get(
        `/api/project-risk-analysis?regionalNetwork=${regionalNetworkOption || ''}&country=${countryOption || ''}`
      );

      if (response.data) {
        const formattedData = response.data.map((risk) => ({
          reviewDate: risk.reviewDate,      
          probability: risk.probability,     
          impact: risk.impact,              
          type: risk.type,                  
        }));

        setRiskData(formattedData);
      }
    } catch (error) {
      console.error('Error fetching risk data:', error);
      setError(error.response?.data?.message || 'Failed to fetch risk data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRisks();
  }, []);

  // Handle changes in the risk type filter
  const handleRiskTypeChange = (event) => {
    setSelectedRiskType(event.target.value);
  };

  // Filter data based on the selected risk type
  const filterDataByRiskType = (data) => {
    return selectedRiskType && selectedRiskType !== 'All'
      ? data.filter((risk) => risk.type === selectedRiskType)
      : data;
  };

  // Convert risk impact and probability to numerical values
  const convertRiskValue = (riskLevel) => {
    switch (riskLevel) {
      case 'Low':
        return 1;
      case 'Medium':
        return 2;
      case 'High':
        return 3;
      default:
        return 0;
    }
  };

  // Apply the filter to the risk data
  const filteredData = filterDataByRiskType(riskData);

  // Format date function
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(new Date(dateString));
  };

  // Prepare data for the chart
  const chartData = {
    labels: filteredData.map((risk) => formatDate(risk.reviewDate)), // X-axis: Formatted Review Date
    datasets: [
      {
        label: 'Probability',
        data: filteredData.map((risk) => convertRiskValue(risk.probability)), // Y-axis: Probability
        fill: false,
        borderColor: 'blue',
        tension: 0.1,
      },
      {
        label: 'Impact',
        data: filteredData.map((risk) => convertRiskValue(risk.impact)), // Y-axis: Impact
        fill: false,
        borderColor: 'red',
        tension: 0.1,
      },
    ],
  };

  // Function to export chart data to PowerPoint
  const exportToPPT = () => {
    const pptx = new pptxgen();
    const slide = pptx.addSlide();
    slide.addText('Risk Impact and Probability Trends', { x: 0.5, y: 0.5, fontSize: 24 });

    // Add chart data to the slide
    slide.addChart(pptx.ChartType.line, [
      {
        name: 'Probability',
        labels: filteredData.map((risk) => formatDate(risk.reviewDate)),
        values: filteredData.map((risk) => convertRiskValue(risk.probability)),
      },
      {
        name: 'Impact',
        labels: filteredData.map((risk) => formatDate(risk.reviewDate)),
        values: filteredData.map((risk) => convertRiskValue(risk.impact)),
      },
    ], { x: 0.5, y: 1.5, w: 8, h: 4 });

    pptx.writeFile({ fileName: 'RiskAnalysis.pptx' });
  };

  // Early return if loading
  if (loading) return <Typography variant="h6">Loading...</Typography>;
  if (error) return <Typography variant="h6" color="error">{error}</Typography>; // Display error message

  return (
    <Container 
      style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', paddingRight: '40px' }}>

      {/* Header */}
      <Typography variant="h4" gutterBottom style={{ textAlign: 'center' }}>
        Risk Impact and Probability Trends
      </Typography>

      {/* Risk Type Filter */}
      <FormControl fullWidth margin="normal">
        <Select value={selectedRiskType} onChange={handleRiskTypeChange}>
          {riskTypes.map((type, index) => (
            <MenuItem key={index} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

     
      
      {/* Line Chart */}
      <Box>
        <Line 
          data={chartData} 
          options={{
            responsive: true,
            maintainAspectRatio: false, // Set this to false to allow flexible resizing
            scales: {
              x: {
                title: {
                  display: true,
                  text: 'Review Date',
                },
              },
              y: {
                title: {
                  display: true,
                  text: 'Risk Level',
                },
                beginAtZero: true,
                ticks: {
                  stepSize: 1, // Adjust step size as needed
                  callback: (value) => {
                    switch (value) {
                      case 1:
                        return 'Low';
                      case 2:
                        return 'Medium';
                      case 3:
                        return 'High';
                      default:
                        return '';
                    }
                  },
                },
              },
            },
          }} 
          style={{ height: '400px', width: '100%' }} // Adjusted height for the chart
        />
        
      </Box>
       {/* Export to PPT Button */}
       <Button variant="contained" color="primary" onClick={exportToPPT} style={{ marginBottom: '20px' }}>
        Export to PPT
      </Button>
    </Container>
  );
};

export default RiskLineChart;